/* eslint-disable no-use-before-define */
import React, { useEffect } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Chip,
  Divider,
  Drawer,
  Hidden,
  Link,
  List,
  ListSubheader,
  makeStyles,
  Container,
  Typography,
  useTheme,
} from '@material-ui/core';
import PeopleAltIcon from '@material-ui/icons/PeopleAlt';
import HomeWorkIcon from '@material-ui/icons/HomeWork';
import BuildIcon from '@material-ui/icons/Build';
import {
  Briefcase as BriefcaseIcon,
  Calendar as CalendarIcon,
} from 'react-feather';
import Logo from 'src/components/Logo';
import useAuth from 'src/hooks/useAuth';
import NavItem from './NavItem';
import {
  APP_VERSION,
  COMPANY_ID_NUM,
  COMPANY_NAME,
  COMPANY_ADDRESS,
  COMPANY_CITY,
  COMPANY_POSTCODE,
  COMPANY_PROVINCE,
  UPDATING_MESSAGE
} from '../../../constants';

const sections = {
  ADMIN: [
    {
      subheader: 'Administración',
      items: [
        {
          title: 'Usuarios',
          icon: PeopleAltIcon,
          href: '/app/users'
        },
        {
          title: 'Siniestros',
          icon: HomeWorkIcon,
          href: '/app/accidents'
        },
        {
          title: 'Tareas',
          icon: BuildIcon,
          href: '/app/myWorks'
        },
        {
          title: 'Aseguradoras',
          icon: BriefcaseIcon,
          href: '/app/insurers'
        },
        {
          title: 'Calendario',
          icon: CalendarIcon,
          href: '/app/calendar'
        }
      ]
    }
  ],
  MANAGER: [
    {
      subheader: 'Administración',
      items: [
        {
          title: 'Usuarios',
          icon: PeopleAltIcon,
          href: '/app/users'
        },
        {
          title: 'Siniestros',
          icon: HomeWorkIcon,
          href: '/app/accidents'
        },
        {
          title: 'Tareas',
          icon: BuildIcon,
          href: '/app/myWorks'
        },
        {
          title: 'Aseguradoras',
          icon: BriefcaseIcon,
          href: '/app/insurers'
        },
        {
          title: 'Calendario',
          icon: CalendarIcon,
          href: '/app/calendar'
        }
      ]
    }
  ],
  OPERATOR: [
    {
      subheader: 'Operario',
      items: [
        {
          title: 'Mis Tareas',
          icon: BuildIcon,
          href: '/app/myWorks'
        }
      ]
    }
  ]
};

function renderNavItems({ items, pathname, depth = 0 }) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({ acc, item, pathname, depth }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({ acc, pathname, item, depth }) {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={Boolean(open)}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        info={item.info}
        key={key}
        title={item.title}
      />
    );
  }

  return acc;
}

const useStyles = makeStyles(() => ({
  mobileDrawer: {
    width: 256
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 64px)'
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64
  }
}));

const NavBar = ({ onMobileClose, openMobile }) => {
  const classes = useStyles();
  const location = useLocation();
  const { user } = useAuth();
  const theme = useTheme();

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const content = (
    <Box height="100%" display="flex" flexDirection="column">
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box p={2} display="flex" justifyContent="center">
            <RouterLink to="/">
              <Logo />
            </RouterLink>
          </Box>
        </Hidden>
        <Box p={2}>
          <Box display="flex" justifyContent="center">
            <RouterLink to="/app/account">
              <Avatar alt="User" className={classes.avatar} src={user.avatar} />
            </RouterLink>
          </Box>
          <Box mt={2} textAlign="center">
            <Link
              component={RouterLink}
              to="/app/account"
              variant="h5"
              color="textPrimary"
              underline="none"
            >
              {user.name}
            </Link>
            {/*
            <Typography
              variant="body2"
              color="textSecondary"
            >
              Your tier:
              {' '}
              <Link
                component={RouterLink}
                to="/pricing"
              >
                {user.tier}
              </Link>
            </Typography>
            */}
          </Box>
        </Box>
        <Divider />
        <Box p={2}>
          {sections[user.role].map(section => (
            <List
              key={section.subheader}
              subheader={
                <ListSubheader disableGutters disableSticky>
                  {section.subheader}
                </ListSubheader>
              }
            >
              {renderNavItems({
                items: section.items,
                pathname: location.pathname
              })}
            </List>
          ))}
        </Box>
        <Divider />
        <Container maxWidth={false}>
          <Box mt={3} bgcolor={theme.palette.background.dark} p={1}>
            <Typography>
              {COMPANY_NAME}<br />
              {COMPANY_ID_NUM}<br />
              {COMPANY_ADDRESS}<br />
              {COMPANY_POSTCODE} {COMPANY_CITY}, {COMPANY_PROVINCE}
            </Typography>
            <Link
              variant="subtitle1"
              color="secondary"
              component={RouterLink}
              to="/version"
            >
              Version {APP_VERSION}
            </Link>
            <Typography/>{UPDATING_MESSAGE}
          </Box>
        </Container>

        {/*
        <Box p={2}>
          <Box
            p={2}
            borderRadius="borderRadius"
            bgcolor="background.dark"
          >
            <Typography
              variant="h6"
              color="textPrimary"
            >
              Need Help?
            </Typography>
            <Link
              variant="subtitle1"
              color="secondary"
              component={RouterLink}
              to="/docs"
            >
              Check our docs
            </Link>
          </Box>
        </Box>
        */}
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default NavBar;
